/// <reference lib="dom"/>

export type WbaxRng = () => number;
export const WbaxNoiseTypes = ["white", "pink", "brownian"] as const;
export type WbaxNoiseType = (typeof WbaxNoiseTypes)[number];

export function FillWhiteNoise(data: Float32Array, rng: WbaxRng) {
  for (let i = 0; i < data.length; i++) data[i] = rng() * 2 - 1;
  return data;
}

export function FillPinkNoise(data: Float32Array, rng: WbaxRng) {
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < data.length; i++) {
    const white = rng() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
    b6 = white * 0.115926;
  }
  return data;
}

export function FillBrownianNoise(data: Float32Array, rng: WbaxRng) {
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    const white = rng() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.5;
  }
  return data;
}

export function FillNoise(data: Float32Array, type: WbaxNoiseType, rng: WbaxRng) {
  switch (type) {
    case "white":
      return FillWhiteNoise(data, rng);
    case "pink":
      return FillPinkNoise(data, rng);
    case "brownian":
      return FillBrownianNoise(data, rng);
  }
}

export interface NoiseBufferProps {
  type?: WbaxNoiseType;
  duration?: number;
  channels?: number;
  rng?: WbaxRng;
}

export function NoiseBuffer(ctx: BaseAudioContext, props: NoiseBufferProps = {}): AudioBuffer {
  const { type = "white", duration = 1, channels = 1, rng = Math.random } = props;
  const length = Math.max(1, Math.round(duration * ctx.sampleRate));
  const buffer = ctx.createBuffer(channels, length, ctx.sampleRate);
  for (let c = 0; c < channels; c++) FillNoise(buffer.getChannelData(c), type, rng);
  return buffer;
}

export function DistortionCurve(amount: number, samples = 44100): Float32Array {
  const curve = new Float32Array(samples);
  const k = Math.max(0, amount);
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
  }
  return curve;
}
